import { useEffect, useRef, useState } from 'react';

export const useSearchbarScrollBehavior = (scrollDownThreshold = 80) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const searchbarRef = useRef<HTMLDivElement>(null);

    const [isScrolledPast, setIsScrolledPast] = useState(false);
    const [isSearchbarHidden, setIsSearchbarHidden] = useState(false);
    const [searchbarHeight, setSearchbarHeight] = useState(0);

    const scrollData = useRef({
        lastY: 0,
        accumulated: 0,
        isScrolledPast: false,
    });

    useEffect(() => {
        const searchbar = searchbarRef.current;
        if (!searchbar) return;
        setSearchbarHeight(searchbar.offsetHeight);
    }, []);

    useEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;

        const intersectionObserver = new IntersectionObserver(
            ([entry]) => {
                const scrolledPast = !entry.isIntersecting;

                setIsScrolledPast(scrolledPast);
                scrollData.current.isScrolledPast = scrolledPast;

                if (!scrolledPast) {
                    setIsSearchbarHidden(false);
                    scrollData.current.accumulated = 0;
                }
            },
            { threshold: 0 },
        );

        intersectionObserver.observe(anchor);
        return () => intersectionObserver.disconnect();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const data = scrollData.current;

            if (!data.isScrolledPast) return;

            const currentY = window.scrollY;
            const scrollChange = currentY - data.lastY;
            data.lastY = currentY;

            if (scrollChange > 0) {
                data.accumulated += scrollChange;
                if (data.accumulated >= scrollDownThreshold) {
                    setIsSearchbarHidden(true);
                }
            } else if (scrollChange < 0) {
                data.accumulated = 0;
                setIsSearchbarHidden(false);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        scrollData.current.lastY = window.scrollY;

        return () => window.removeEventListener('scroll', handleScroll);
    }, [scrollDownThreshold]);

    return {
        anchorRef,
        searchbarRef,
        isScrolledPast,
        searchbarTransform: isSearchbarHidden ? `translateY(-${searchbarHeight}px)` : undefined,
    };
};
